/**
 * ============================================================================
 * THEME GENERATOR
 * ============================================================================
 * 
 * Generates complete CSS theme variables from user input colors
 */

import {
  hexToRgb,
  lightenColor,
  darkenColor,
  withOpacity,
  getContrastText
} from './color-utils';

/**
 * Generates a COMPLETE theme from user-selected colors
 * @param {Object} userColors - User colors
 * @param {string} userColors.primary - Primary color (#hex)
 * @param {string} [userColors.textOnPrimary] - Text color on primary (optional, auto-calculated)
 * @param {string} [userColors.headerBg] - Header background color (optional, uses primary by default)
 * @param {boolean} [userColors.isWhitelabel] - Whether this is a whitelabel user
 * @returns {Object} Complete object with ALL CSS variables
 */
export function generateFullThemeFromUserInput(userColors) {
  const { primary, textOnPrimary, headerBg } = userColors;
  
  // 1. Calculate text on primary (white or black based on luminance)
  const textColor = textOnPrimary || getContrastText(primary);
  
  // 2. Header color (if not specified, use primary)
  // Can be a solid color (#hex) or a gradient (linear-gradient(...))
  const headerColor = headerBg || primary;
  const isGradient = typeof headerColor === 'string' && headerColor.includes('gradient');
  
  // 3. Generate all primary variants
  const primaryLight = lightenColor(primary, 15);
  const primaryDark = darkenColor(primary, 20);
  const primaryHover = darkenColor(primary, 10);
  const primaryBorder = withOpacity(primary, 0.3);
  const primaryShadow = withOpacity(primaryDark, 0.2);
  const primarySubtle = withOpacity(primary, 0.1);
  const primaryMuted = withOpacity(primary, 0.3);
  
  // 4. Generate header variants
  // If gradient, we can't darken it, use dark primary as hover
  const headerHover = isGradient ? primaryDark : darkenColor(headerColor, 8);
  const headerBorder = isGradient ? primaryBorder : withOpacity(headerColor, 0.2);
  const headerTextColor = isGradient ? '#ffffff' : getContrastText(headerColor);
  
  // Extract RGB values for Bootstrap
  const rgb = hexToRgb(primary);
  const rgbString = rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '115, 103, 240';
  
  const themeVars = {
    // ========== BOOTSTRAP OVERRIDES ==========
    '--bs-primary': primary,
    '--bs-primary-rgb': rgbString,
    '--primary': primary, // Bootstrap 4 fallback
    
    // ========== BASE COLORS ==========
    '--brand-primary': primary,
    '--brand-primary-rgb': rgbString,
    '--brand-primary-light': primaryLight,
    '--brand-primary-dark': primaryDark,
    '--brand-primary-hover': primaryHover,
    '--brand-primary-text': textColor,
    '--primary-shadow-blue': `${withOpacity(primaryLight, 0.3)} 0px 7px 29px 0px`,
    '--primary-gradient-button': primary ? `linear-gradient(to left bottom, ${primary}, ${primaryDark})` : null,
    '--primary-gradient-button-hover': primary ? `linear-gradient(to left bottom, ${withOpacity(primary, 0.9)}, ${withOpacity(primaryDark, 0.85)})` : null,
    '--primary-for-blue-cases': primary, // Used for utility classes
    
    // ========== PRIMARY BORDERS AND SHADOWS ==========
    '--primary-border': primaryBorder,
    '--primary-shadow': primaryShadow,
    '--primary-subtle-bg': primarySubtle,
    '--primary-muted-bg': primaryMuted,
    
    // ========== BUTTONS ==========
    '--btn-primary-bg': primary,
    '--btn-primary-bg-hover': primaryHover,
    '--btn-primary-bg-active': primaryDark,
    '--btn-primary-text': textColor,
    '--btn-primary-border': primary,
    '--btn-primary-shadow': `0 2px 8px ${primaryShadow}`,
    '--btn-primary-shadow-hover': `0 4px 12px ${withOpacity(primaryDark, 0.3)}`,
    
    // ========== INPUTS ==========
    '--input-border-focus': primary,
    '--input-shadow-focus': `0 0 0 3px ${withOpacity(primary, 0.2)}`,
    '--input-bg-focus': withOpacity(primary, 0.05),
    
    // ========== BADGES ==========
    '--badge-primary-bg': primary,
    '--badge-primary-text': textColor,
    '--badge-primary-light-bg': primarySubtle,
    '--badge-primary-light-text': primary,
    
    // ========== CARDS WITH PRIMARY ACCENT ==========
    '--card-accent-border': primary,
    '--card-accent-bg': primarySubtle,
    '--card-hover-shadow': `0 4px 16px ${primaryShadow}`,
    
    // ========== LINKS ==========
    '--link-color': primary,
    '--link-hover': primaryDark,
    '--link-active': primaryDark,
    
    // ========== HEADER / NAVBAR ==========
    '--header-bg': headerColor,
    '--header-bg-hover': headerHover,
    '--header-text': headerTextColor,
    '--header-text-hover': withOpacity(headerTextColor, 0.5),
    '--header-border': headerBorder,
    '--header-shadow': isGradient ? '0 2px 8px rgba(0,0,0,0.15)' : `0 2px 8px ${withOpacity(headerColor, 0.15)}`,

    
    // ========== SIDEBAR (uses primary variants) ==========
    '--sidebar-bg': primary,
    '--sidebar-bg-hover': primaryHover,
    '--sidebar-text': textColor,
    '--sidebar-text-active': textColor,
    '--sidebar-item-active-bg': primaryDark,
    '--sidebar-border': primaryBorder,
    
    // ========== STATES / FOCUS ==========
    '--focus-ring': `0 0 0 3px ${withOpacity(primary, 0.25)}`,
    '--focus-border': primary,
    
    // ========== TABS / NAV ==========
    '--nav-item-active-color': primary,
    '--nav-item-active-border': primary,
    '--nav-item-hover-bg': primarySubtle,
    
    // ========== PROGRESS / SLIDERS ==========
    '--progress-bar-bg': primary,
    '--slider-thumb-bg': primary,
    '--slider-track-active': primary,
    
    // ========== ALERTS / TOASTS (info variant) ==========
    '--alert-info-bg': primarySubtle,
    '--alert-info-text': primaryDark,
    '--alert-info-border': primaryBorder
  };
  
  return themeVars;
}
